import { LOGO_URL } from "@/lib/api";

export const SITE_URL = "https://embersteelstudio.ca";
export const BUSINESS = {
  name: "Ember & Steel Studio",
  phone: "+1-780-603-3400",
  phoneDisplay: "(780) 603-3400",
  email: "sales@embersteelstudio.ca",
  locality: "Leduc",
  region: "AB",
  country: "CA",
};

export const SERVICE_AREAS = ["Leduc", "Nisku", "Beaumont", "Edmonton", "Devon", "Calmar", "Sherwood Park", "St. Albert"];
export const SHIPPING_SENTENCE = "Ships across Canada and to the USA by Canada Post — shipping is calculated at checkout.";

const KNOWS_ABOUT = ["Laser engraving", "Fiber laser engraving on stainless steel", "UV printing", "Corporate gifts", "Branded merchandise", "Metal business cards", "Industrial asset tags", "Personalized drinkware"];

export const orgSchema = (sameAs = []) => ({
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "OnlineStore"],
  "@id": `${SITE_URL}/#business`,
  name: BUSINESS.name,
  url: SITE_URL,
  logo: LOGO_URL,
  image: LOGO_URL,
  email: BUSINESS.email,
  telephone: BUSINESS.phone,
  description: "Custom laser engraving, UV printing, personalized drinkware, corporate gifts and custom metal products made in Leduc, Alberta for Edmonton-area customers, shipped across Canada and to the USA.",
  address: { "@type": "PostalAddress", addressLocality: BUSINESS.locality, addressRegion: BUSINESS.region, addressCountry: BUSINESS.country },
  areaServed: [
    ...SERVICE_AREAS.map((n) => ({ "@type": "City", name: `${n}, Alberta` })),
    { "@type": "State", name: "Alberta" },
    { "@type": "Country", name: "Canada" },
  ],
  knowsAbout: KNOWS_ABOUT,
  priceRange: "$$",
  currenciesAccepted: "CAD",
  paymentAccepted: "Credit Card",
  ...(sameAs.length ? { sameAs } : {}),
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${SITE_URL}${path}` })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
});

export const serviceSchema = ({ name, description, path, serviceType, areaServed }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  serviceType,
  description,
  url: `${SITE_URL}${path}`,
  provider: { "@id": `${SITE_URL}/#business` },
  areaServed: areaServed || [...SERVICE_AREAS.map((n) => ({ "@type": "City", name: `${n}, Alberta` })), { "@type": "Country", name: "Canada" }],
});

export const collectionSchema = ({ name, description, path, items }) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name,
  description,
  url: `${SITE_URL}${path}`,
  isPartOf: { "@type": "WebSite", url: SITE_URL, name: BUSINESS.name },
  mainEntity: {
    "@type": "ItemList",
    numberOfItems: items.length,
    itemListElement: items.map(([itemName, itemPath], i) => ({ "@type": "ListItem", position: i + 1, name: itemName, url: `${SITE_URL}${itemPath}` })),
  },
});

const productBrand = (name) => (/yeti/i.test(name) ? "YETI" : /stanley/i.test(name) ? "Stanley" : BUSINESS.name);

const shippingDetails = ["CA", "US"].map((country) => ({
  "@type": "OfferShippingDetails",
  shippingDestination: { "@type": "DefinedRegion", addressCountry: country },
  deliveryTime: {
    "@type": "ShippingDeliveryTime",
    handlingTime: { "@type": "QuantitativeValue", minValue: 5, maxValue: 7, unitCode: "DAY" },
    transitTime: { "@type": "QuantitativeValue", minValue: 2, maxValue: 9, unitCode: "DAY" },
  },
}));

export const productSchema = (p) => {
  const prices = [p.amount, ...(p.variants || []).map((v) => v.amount)].filter(Boolean);
  const lo = Math.min(...prices) / 100;
  const hi = Math.max(...prices) / 100;
  const url = `${SITE_URL}/shop/${p.slug}`;
  const priceValidUntil = `${new Date().getFullYear() + 1}-01-31`;
  const common = { url, priceCurrency: "CAD", availability: "https://schema.org/InStock", itemCondition: "https://schema.org/NewCondition", seller: { "@id": `${SITE_URL}/#business` }, shippingDetails, priceValidUntil };
  const offers = lo === hi
    ? { "@type": "Offer", price: lo.toFixed(2), ...common }
    : { "@type": "AggregateOffer", lowPrice: lo.toFixed(2), highPrice: hi.toFixed(2), offerCount: prices.length, ...common };
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    description: p.description,
    image: p.image,
    sku: p.slug,
    brand: { "@type": "Brand", name: productBrand(p.name) },
    material: p.material,
    category: p.method === "UV Printed" ? "UV printed keepsakes" : "Laser engraved gifts",
    url,
    offers,
  };
};

const FILLER = /\s+(and|or|a|an|the|of|to|your|with|for|in|on|by|at|from|that|which|is|are|then|as|into|our|its|their|—|–)$/i;

export const productMetaDescription = (p) => {
  const suffix = " Made in Leduc, Alberta · ships across Canada and to the USA.";
  const text = (p.description || "").trim();
  const clip = (str, budget) => {
    if (str.length <= budget) return str;
    const cut = str.slice(0, budget);
    const end = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("! "), cut.lastIndexOf("? "));
    if (end > 40) return cut.slice(0, end + 1);
    const clause = Math.max(cut.lastIndexOf(", "), cut.lastIndexOf("; "), cut.lastIndexOf(" — "));
    let head = clause > 60 ? cut.slice(0, clause) : cut.slice(0, cut.lastIndexOf(" "));
    for (let i = 0; i < 3 && FILLER.test(head); i += 1) head = head.replace(FILLER, "");
    return `${head.replace(/[\s,;:—–-]+$/, "")}…`;
  };
  const lead = p.subtitle ? `${p.subtitle}. ` : "";
  const budget = 155 - suffix.length - lead.length;
  if (lead && (budget < 70 || text.length > budget)) return `${clip(text, 155 - suffix.length)}${suffix}`;
  return `${lead}${clip(text, budget)}${suffix}`;
};
